import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface Props {
    evictionsByMonth: [string, number][];
    preventionsByMonth: [string, number][];
}

const monthOrder = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

const AreaChart = ({ evictionsByMonth, preventionsByMonth }: Props) => {
    const [chartData, setChartData] = useState<any>({ labels: [], datasets: [] });

    useEffect(() => {
        const allMonths = Array.from(
            new Set([
                ...evictionsByMonth.map(([month]) => month),
                ...preventionsByMonth.map(([month]) => month),
            ])
        );

        allMonths.sort((a, b) => {
            const [yearA, monthA] = a.split("-");
            const [yearB, monthB] = b.split("-");
            return yearA !== yearB
                ? Number(yearA) - Number(yearB)
                : monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
        });

        const evictionValues = allMonths.map(
            (month) => evictionsByMonth.find(([m]) => m === month)?.[1] || 0
        );
        const preventionValues = allMonths.map(
            (month) => preventionsByMonth.find(([m]) => m === month)?.[1] || 0
        );

        setChartData({
            labels: allMonths,
            datasets: [
                {
                    label: "Evictions",
                    data: evictionValues,
                    backgroundColor: "rgba(139, 92, 246, 0.2)", // Purple with opacity
                    borderColor: "#8b5cf6", // Purple
                    borderWidth: 1,
                    pointBackgroundColor: "#fff",
                    pointBorderColor: "#8b5cf6",
                    pointBorderWidth: 1,
                    pointHoverRadius: 5,
                    pointHoverBorderWidth: 1,
                    fill: true,
                    tension: 0.3, // Smooth curve
                },
                {
                    label: "Preventions",
                    data: preventionValues,
                    backgroundColor: "rgba(34, 197, 94, 0.2)", // Green with opacity
                    borderColor: "#22c55e", // Green
                    borderWidth: 1,
                    pointBackgroundColor: "#fff",
                    pointBorderColor: "#22c55e",
                    pointBorderWidth: 1,
                    pointHoverRadius: 5,
                    pointHoverBorderWidth: 1,
                    fill: true,
                    tension: 0.3, // Smooth curve
                },
            ],
        });
    }, [evictionsByMonth, preventionsByMonth]);

    return (
        <div className="p-2 bg-white rounded-md">
            {evictionsByMonth.length === 0 && preventionsByMonth.length === 0 ? (
                <div className="flex items-center justify-center h-64 gap-2 text-sm text-gray-500">
                    <svg
                        className="w-5 h-5 animate-spin text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                    </svg>
                    Loading chart data...
                </div>
            ) : (
                <div style={{ minHeight: "300px", width: "100%" }}>
                    <Line
                        data={chartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            elements: {
                                line: {
                                    borderWidth: 0, // Remove outer border
                                    tension: 0.3 // Maintain smooth transitions
                                },
                                point: {
                                    radius: 3,
                                    hoverRadius: 5
                                }
                            },
                            plugins: {
                                title: {
                                    display: true,
                                    text: "Monthly Evictions and Preventions Trends",
                                    font: {
                                        size: 12,
                                        weight: "bold",
                                    },
                                    color: "#333",
                                    padding: { top: 0, bottom: 10 },
                                },
                                legend: {
                                    display: true,
                                    position: "top",
                                    labels: {
                                        boxWidth: 12,
                                        boxHeight: 12,
                                        padding: 10,
                                        font: {
                                            size: 10,
                                        },
                                        usePointStyle: true,
                                    },
                                },
                                tooltip: {
                                    backgroundColor: "#fff",
                                    borderColor: "#ddd",
                                    borderWidth: 1,
                                    titleColor: "#333",
                                    bodyColor: "#333",
                                    padding: 10,
                                    callbacks: {
                                        label: (context) => {
                                            const value = context.raw as number; // cast since TS sees raw as unknown
                                            return `${context.dataset.label}: ${value.toLocaleString()}`;
                                        },
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    grid: {
                                        display: false,
                                    },
                                    ticks: {
                                        font: {
                                            size: 10,
                                        },
                                        color: "#666",
                                    },
                                },
                                y: {
                                    beginAtZero: true,
                                    grid: {
                                        color: "rgba(180, 180, 180, 0.1)",
                                    },
                                    ticks: {
                                        font: {
                                            size: 10,
                                        },
                                        color: "#666",
                                        callback: function (value) {
                                            if (typeof value === "number") {
                                                if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
                                                if (value >= 1_000) return (value / 1_000).toFixed(0) + 'K';
                                                return value.toString();
                                            }
                                            return value;
                                        },
                                    },
                                },
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default AreaChart;